export type TimestampValue =
  | Date
  | string
  | number
  | { toDate: () => Date }
  | { seconds: number; nanoseconds?: number };

export function parseTimestamp(value: unknown): Date | null {
  let date: Date;

  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'string' || typeof value === 'number') {
    date = new Date(value);
  } else if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
    try {
      date = value.toDate();
    } catch {
      return null;
    }
    if (!(date instanceof Date)) return null;
  } else if (value && typeof value === 'object' && 'seconds' in value && typeof value.seconds === 'number') {
    const milliseconds = value.seconds * 1000 + ('nanoseconds' in value && typeof value.nanoseconds === 'number' ? value.nanoseconds / 1_000_000 : 0);
    date = new Date(milliseconds);
  } else {
    return null;
  }

  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatRelativeTime(value: unknown, referenceTime: number | Date | null = Date.now()): string {
  const createdAt = parseTimestamp(value);
  if (!createdAt || referenceTime === null) return '';

  const referenceMilliseconds = referenceTime instanceof Date ? referenceTime.getTime() : referenceTime;
  if (!Number.isFinite(referenceMilliseconds)) return '';

  const elapsedMinutes = Math.max(0, Math.floor((referenceMilliseconds - createdAt.getTime()) / 60_000));
  if (elapsedMinutes < 1) return 'just now';
  if (elapsedMinutes < 60) return `${elapsedMinutes} ${elapsedMinutes === 1 ? 'minute' : 'minutes'} ago`;

  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) return `${elapsedHours} ${elapsedHours === 1 ? 'hour' : 'hours'} ago`;

  const elapsedDays = Math.floor(elapsedHours / 24);
  if (elapsedDays < 30) return `${elapsedDays} ${elapsedDays === 1 ? 'day' : 'days'} ago`;
  if (elapsedDays < 365) {
    const elapsedMonths = Math.min(11, Math.floor(elapsedDays / 30));
    return `${elapsedMonths} ${elapsedMonths === 1 ? 'month' : 'months'} ago`;
  }

  const elapsedYears = Math.floor(elapsedDays / 365);
  return `${elapsedYears} ${elapsedYears === 1 ? 'year' : 'years'} ago`;
}

export function formatExactDateTime(value: unknown): string {
  return parseTimestamp(value)?.toLocaleString() || 'Pending timestamp';
}
